import React from "react"

import { Badge } from "@coval/ui/badge"
import { Button } from "@coval/ui/button"
import { Tooltip } from "@coval/ui/tooltip"
import { Clock, RotateCw, X } from "lucide-react"

import {
  MetricErrorStatus,
  type MetricErrorStatusType,
} from "@/types/api/MetricOutput"
import type { MetricsOnlyLaunchConfig } from "@/types/LaunchConfig"
import type {
  MetricRecordData,
  TargetCondition,
} from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"
import { formatMetricDisplayName } from "@/utils/formatting/format-metric-display-results"
import { formatNumber } from "@/utils/formatting/format-number"

import {
  getMetricColor,
  getMetricColorForMetric,
  MetricValueChip,
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "../../metric-display"

export const AUDIO_METRIC_TRANSCRIPT_ERROR =
  "Cannot run audio metrics on a transcript with no audio"
const QUEUED_RUN_STATUS = "QUEUED" as const
const MISSING_VALUE_PLACEHOLDER = "--"

export function renderStatusBasedBadge(
  errorMessage: string,
  errorStatus: string,
  rerunConfig: MetricsOnlyLaunchConfig,
  reRunMetric?: (launchConfig: MetricsOnlyLaunchConfig) => Promise<unknown>,
): React.ReactNode {
  if (errorMessage?.includes(AUDIO_METRIC_TRANSCRIPT_ERROR)) {
    return (
      <div className="flex items-center justify-center">
        <Tooltip content="No audio to run metric on or the transcript could not be computed.">
          <div className="flex items-center justify-center">
            {MISSING_VALUE_PLACEHOLDER}
          </div>
        </Tooltip>
        {reRunMetric && (
          <Button
            icon
            variant="ghost"
            tooltip="Rerun metric"
            onClick={() => {
              void reRunMetric?.(rerunConfig)
            }}
            className="min-w-0 p-1"
          >
            <RotateCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  if (errorStatus === "CANCELLED") {
    return (
      <div className="text-blue-500 flex items-center justify-center">
        {reRunMetric && (
          <Button
            icon
            tooltip="Run was canceled"
            variant="ghost"
            aria-label="Rerun metric"
            onClick={() => {
              void reRunMetric?.(rerunConfig)
            }}
            className="min-w-0 p-1"
          >
            <RotateCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  if (errorStatus === MetricErrorStatus.SKIPPED) {
    return (
      <div className="flex items-center justify-center">
        <Tooltip content={errorMessage || "Metric was skipped"}>
          <div className="text-gray-500 flex items-center justify-center">
            —
          </div>
        </Tooltip>
        {reRunMetric && (
          <Button
            icon
            variant="ghost"
            tooltip="Rerun metric"
            onClick={() => {
              void reRunMetric?.(rerunConfig)
            }}
            className="min-w-0 p-1"
          >
            <RotateCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  if (
    errorStatus !== MetricErrorStatus.COMPLETED &&
    errorStatus !== MetricErrorStatus.IN_PROGRESS &&
    errorStatus !== QUEUED_RUN_STATUS &&
    errorStatus !== MetricErrorStatus.IN_QUEUE
  ) {
    return (
      <div className="flex items-center justify-center">
        <Tooltip content={errorMessage || "Metric encountered an error"}>
          <div className="text-red-500 flex items-center justify-center">
            <X className="h-5 w-5" />
          </div>
        </Tooltip>
        {reRunMetric && (
          <Button
            icon
            variant="ghost"
            tooltip="Rerun metric"
            onClick={() => {
              void reRunMetric?.(rerunConfig)
            }}
            className="min-w-0 p-1"
          >
            <RotateCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  if (errorStatus === MetricErrorStatus.IN_PROGRESS) {
    return (
      <div className="flex items-center justify-center">
        {/* The spinner carries the status itself, so a read-only consumer with
            no rerun button still learns why the cell is empty. */}
        <Tooltip content="Metric is still running">
          <svg
            className="h-5 w-5 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            role="img"
            aria-label="Metric is still running"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </Tooltip>
        {reRunMetric && (
          <Button
            icon
            tooltip="Rerun metric"
            variant="ghost"
            onClick={() => {
              void reRunMetric?.(rerunConfig)
            }}
            className="min-w-0 p-1"
          >
            <RotateCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    )
  }

  if (
    errorStatus === QUEUED_RUN_STATUS ||
    errorStatus === MetricErrorStatus.IN_QUEUE
  ) {
    return (
      <Tooltip content="Metric is queued for processing">
        <div className="flex items-center justify-center">
          <Clock className="h-5 w-5 text-gray-500" />
        </div>
      </Tooltip>
    )
  }

  return null
}

export function getDefaultDisplayValueString(
  mo: MetricOutputDisplaySource,
): string | null {
  if (mo.output_type === "string") {
    return mo.string_value ?? null
  }
  if (mo.output_type === "float") {
    return mo.float_value != null && Number.isFinite(mo.float_value)
      ? String(mo.float_value)
      : null
  }
  if (mo.output_type === "set" && mo.set_value) {
    return mo.set_value.join(", ")
  }
  return null
}

export function getDefaultHumanReviewDisplayValue(review: {
  output_type: string
  float_value: number | null
  string_value: string | null
  set_value: string[] | null
  json_value: Record<string, unknown> | null
}): string | null {
  if (review.output_type === "float") {
    return review.float_value != null && Number.isFinite(review.float_value)
      ? String(review.float_value)
      : null
  }
  if (review.output_type === "string") {
    return review.string_value
  }
  if (review.output_type === "set") {
    return review.set_value && review.set_value.length > 0
      ? review.set_value.join(", ")
      : null
  }
  return null
}

export function renderDefaultSimulationCard(
  mo: RunMetricOutput,
  metricTargetCondition: TargetCondition | undefined,
  unit: string,
  metric?: MetricRecordData,
): React.ReactNode {
  const value = mo.output_type === "float" ? mo.float_value : mo.string_value
  const unitSuffix = unit ? ` ${unit}` : ""

  if (["float", "string"].includes(mo.output_type)) {
    const hasValue =
      mo.output_type === "float"
        ? mo.float_value != null && Number.isFinite(mo.float_value)
        : !!mo.string_value
    const color = !hasValue
      ? "default"
      : metric
        ? getMetricColorForMetric(value, metric)
        : getMetricColor(value, metricTargetCondition)
    const displayValue =
      mo.output_type === "float"
        ? mo.float_value != null && Number.isFinite(mo.float_value)
          ? formatNumber(mo.float_value)
          : MISSING_VALUE_PLACEHOLDER
        : mo.string_value
          ? formatMetricDisplayName(mo.string_value)
          : MISSING_VALUE_PLACEHOLDER
    const fullText = `${displayValue}${hasValue ? unitSuffix : ""}`

    return (
      <MetricValueChip
        value={fullText}
        colorClassName={yesNoBadgeColorClasses[color]}
      />
    )
  }

  if (mo.output_type === "set" && mo.set_value) {
    const fullText = mo.set_value
      .map((v) => formatMetricDisplayName(v))
      .join(", ")
    return (
      <MetricValueChip
        value={fullText}
        colorClassName={yesNoBadgeColorClasses.default}
      />
    )
  }

  return (
    <span
      className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
    >
      {MISSING_VALUE_PLACEHOLDER}
    </span>
  )
}

export function renderDefaultBadge(
  metricName: string,
  renderSimulationCard: (mo: RunMetricOutput) => React.ReactNode,
  mo: RunMetricOutput,
  reRunMetric?: (launchConfig: MetricsOnlyLaunchConfig) => Promise<unknown>,
  hasValidValue = true,
): React.ReactNode {
  const reRunConfig = {
    simulation_output_ids: [mo.simulation_output_id],
    metrics: {
      [metricName]: {},
    },
    run_type: "metrics_only",
  } satisfies MetricsOnlyLaunchConfig

  const statusBasedBadge = renderStatusBasedBadge(
    mo.error_message || "",
    mo.error_status || "",
    reRunConfig,
    reRunMetric,
  )
  if (statusBasedBadge) {
    return statusBasedBadge
  }

  const valueNode = hasValidValue ? (
    renderSimulationCard(mo)
  ) : (
    <Badge size="chip">--</Badge>
  )

  if (reRunMetric) {
    return (
      <div className="flex items-center justify-center gap-2">
        {valueNode}
        <Button
          icon
          variant="ghost"
          tooltip="Rerun metric"
          onClick={() => {
            void reRunMetric?.(reRunConfig)
          }}
          className="min-w-0 p-1"
        >
          <RotateCw className="h-3 w-3" />
        </Button>
      </div>
    )
  }

  return <div className="flex items-center justify-center">{valueNode}</div>
}

export function renderValueOnlyTableCell(
  metricId: string,
  renderBadge: (
    mo: RunMetricOutput,
    reRunMetric?: (launchConfig: MetricsOnlyLaunchConfig) => Promise<unknown>,
  ) => React.ReactNode,
  value: number | string,
  metricErrorStatus: string,
  errorMessage?: string,
): React.ReactNode {
  const isStringValue = typeof value === "string"
  const outputType = isStringValue ? "string" : "float"

  const fakeMetricOutput: RunMetricOutput = {
    organization_id: "fake_organization_id",
    simulation_output_id: "fake_simulation_output_id",
    metric_id: metricId,
    string_value: isStringValue ? value : null,
    set_value: null,
    subvalues_by_timestamp: [],
    output_type: outputType,
    float_value: isStringValue ? null : value,
    error_status: metricErrorStatus as MetricErrorStatusType,
    error_message: errorMessage || "",
    result_json: {},
    result: null,
    runtime_metadata: {},
  }
  return renderBadge(fakeMetricOutput, undefined)
}
